"use server"

import { sdk } from "@lib/config"
import {
  CatalogFilters,
  getCatalogFilterOptions,
  productMatchesFilters,
} from "@lib/util/product-specs"
import { sortProducts } from "@lib/util/sort-products"
import {
  getUsedFilterOptions,
  isUsedListing,
  productMatchesUsedFilters,
  UsedFilterOptions,
  UsedFilters,
} from "@lib/util/used-listing"
import { HttpTypes } from "@medusajs/types"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { getAuthHeaders } from "./cookies"
import { getRegion, retrieveRegion } from "./regions"

export const listProducts = async ({
  pageParam = 1,
  queryParams,
  countryCode,
  regionId,
}: {
  pageParam?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  countryCode?: string
  regionId?: string
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
}> => {
  if (!countryCode && !regionId) {
    throw new Error("Country code or region ID is required")
  }

  const limit = queryParams?.limit || 12
  const _pageParam = Math.max(pageParam, 1)
  const offset = _pageParam === 1 ? 0 : (_pageParam - 1) * limit

  let region: HttpTypes.StoreRegion | undefined | null

  if (countryCode) {
    region = await getRegion(countryCode)
  } else {
    region = await retrieveRegion(regionId!)
  }

  if (!region) {
    return {
      response: { products: [], count: 0 },
      nextPage: null,
    }
  }

  const headers = {
    ...(await getAuthHeaders()),
  }

  // Listing uses no-store so import/metadata updates (specs, prices) show immediately.
  // Prefer force-cache + revalidateTag in production once catalog data is stable.
  return sdk.client
    .fetch<{ products: HttpTypes.StoreProduct[]; count: number }>(
      `/store/products`,
      {
        method: "GET",
        query: {
          limit,
          offset,
          region_id: region?.id,
          fields:
            "*variants.calculated_price,+variants.inventory_quantity,*variants.images,+metadata,+tags,*categories,",
          ...queryParams,
        },
        headers,
        cache: "no-store",
      }
    )
    .then(({ products, count }) => {
      const nextPage = count > offset + limit ? pageParam + 1 : null

      return {
        response: {
          products,
          count,
        },
        nextPage: nextPage,
        queryParams,
      }
    })
}

function shuffleProducts<T>(items: T[]): T[] {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

/**
 * Pick a random slice of catalog products for home featured picks.
 * Two lightweight calls: count first, then a random offset pool + shuffle.
 */
export const listRandomProducts = async ({
  countryCode,
  limit = 12,
}: {
  countryCode: string
  limit?: number
}): Promise<HttpTypes.StoreProduct[]> => {
  const {
    response: { count },
  } = await listProducts({
    countryCode,
    queryParams: { limit: 1, fields: "id" },
  })

  if (!count) {
    return []
  }

  const poolSize = Math.min(limit * 4, count)
  const offset =
    count > poolSize ? Math.floor(Math.random() * (count - poolSize + 1)) : 0

  const {
    response: { products },
  } = await listProducts({
    countryCode,
    queryParams: {
      limit: poolSize,
      offset,
      fields: "*variants.calculated_price,+metadata,*images",
    },
  })

  return shuffleProducts(
    products.filter(
      (product) => Boolean(product.thumbnail) && !isUsedListing(product)
    )
  ).slice(0, limit)
}

/**
 * Fetch the full matching catalog so brand/spec filters and client pagination
 * are not truncated to the first API page (default was 100).
 */
async function listAllProductsForCatalog({
  queryParams,
  countryCode,
}: {
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  countryCode: string
}): Promise<HttpTypes.StoreProduct[]> {
  const pageSize = 250
  const {
    response: { products: firstPage, count },
  } = await listProducts({
    pageParam: 1,
    queryParams: {
      ...queryParams,
      limit: pageSize,
    },
    countryCode,
  })

  if (firstPage.length >= count) {
    return firstPage
  }

  const products = [...firstPage]
  const remaining = Math.ceil((count - firstPage.length) / pageSize)
  const pages = await Promise.all(
    Array.from({ length: remaining }, (_, index) =>
      listProducts({
        pageParam: index + 2,
        queryParams: {
          ...queryParams,
          limit: pageSize,
        },
        countryCode,
      })
    )
  )

  for (const page of pages) {
    products.push(...page.response.products)
  }

  return products
}

export type ListingZone = "catalog" | "used"

export const listProductsWithSort = async ({
  page = 0,
  queryParams,
  sortBy = "created_at",
  countryCode,
  filters,
  zone = "catalog",
  usedFilters,
}: {
  page?: number
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  sortBy?: SortOptions
  countryCode: string
  filters?: CatalogFilters
  zone?: ListingZone
  usedFilters?: UsedFilters
}): Promise<{
  response: { products: HttpTypes.StoreProduct[]; count: number }
  nextPage: number | null
  queryParams?: HttpTypes.FindParams & HttpTypes.StoreProductListParams
  filterOptions: ReturnType<typeof getCatalogFilterOptions>
  usedFilterOptions: UsedFilterOptions
}> => {
  const limit = queryParams?.limit || 12

  const products = await listAllProductsForCatalog({
    queryParams,
    countryCode,
  })

  const zonedProducts = products.filter((product) =>
    zone === "used" ? isUsedListing(product) : !isUsedListing(product)
  )
  const sortedProducts = sortProducts(zonedProducts, sortBy)
  const filterOptions = getCatalogFilterOptions(sortedProducts, filters || {})
  const usedFilterOptions = getUsedFilterOptions(sortedProducts)
  const filteredProducts =
    zone === "used"
      ? sortedProducts.filter((product) =>
          productMatchesUsedFilters(product, usedFilters || {})
        )
      : filters
        ? sortedProducts.filter((product) =>
            productMatchesFilters(product, filters)
          )
        : sortedProducts

  const pageParam = (page - 1) * limit

  const nextPage =
    filteredProducts.length > pageParam + limit ? pageParam + limit : null

  const paginatedProducts = filteredProducts.slice(pageParam, pageParam + limit)

  return {
    response: {
      products: paginatedProducts,
      count: filteredProducts.length,
    },
    nextPage,
    queryParams,
    filterOptions,
    usedFilterOptions,
  }
}
